import React, { useState } from "react";
import { FLISERENS_PACKAGES } from "../data/siteData";

export default function FliserensCalculator({ onSelectCalculation }) {
  const [areaM2, setAreaM2] = useState(65);
  const [selectedPackageId, setSelectedPackageId] = useState("premium");

  const selectedPackage =
    FLISERENS_PACKAGES.find((pkg) => pkg.id === selectedPackageId) ||
    FLISERENS_PACKAGES[1];

  const estimatedTotal = Math.round(areaM2 * selectedPackage.pricePerM2);

  const presets = [
    { label: "Lille terrasse", m2: 30 },
    { label: "Standard terrasse", m2: 60 },
    { label: "Stor terrasse & gang", m2: 90 },
    { label: "Terrasse & indkørsel", m2: 150 }
  ];

  const handleApplyToForm = () => {
    if (onSelectCalculation) {
      onSelectCalculation({
        areaM2,
        packageName: selectedPackage.name,
        pricePerM2: selectedPackage.pricePerM2,
        estimatedTotal
      });
    }
    const formEl = document.getElementById("kontakt");
    if (formEl) {
      formEl.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div id="tilbud" className="bg-white rounded-2xl shadow-xl border border-slate-200/80 p-6 sm:p-8 lg:p-10 scroll-mt-28 sm:scroll-mt-32">
      <div className="max-w-3xl mx-auto text-center mb-8">
        <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
          Beregn din pris på fliserens
        </h3>
        <p className="mt-2 text-slate-600 text-sm sm:text-base">
          Træk i skyderen eller indtast dit forventede areal i m² og vælg den ønskede behandlingspakke.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Side: Calculation Result Box */}
        <div className="lg:col-span-5 bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-950 rounded-none p-6 sm:p-7 text-white shadow-xl flex flex-col justify-between h-full">
          <div>
            <div className="flex items-center justify-between border-b border-slate-700/80 pb-4 mb-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-white">
                Overslag &amp; Sammenfatning
              </span>
              <span className="text-xs text-white font-medium">
                Inkl. moms
              </span>
            </div>

            <div className="space-y-3 text-sm text-slate-300">
              <div className="flex justify-between items-center">
                <span>Valgt areal:</span>
                <span className="font-bold text-white text-base">{areaM2} m²</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Pakke:</span>
                <span className="font-bold text-white">{selectedPackage.name} ({selectedPackage.pricePerM2} kr./m²)</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Inkluderet:</span>
                <span className="text-xs text-slate-300 text-right max-w-[200px]">
                  Rens {selectedPackage.id !== "basis" ? "+ Algebeh. + Imprægnering" : "+ Skylning"}
                </span>
              </div>
            </div>

            <div className="my-6 pt-5 border-t border-slate-700">
              <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">
                Estimeret samlet pris
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl sm:text-4xl font-extrabold text-white">
                  {estimatedTotal.toLocaleString("da-DK")} kr.
                </span>
                <span className="text-xs text-slate-400">inkl. moms</span>
              </div>
              <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                * Dette er et vejledende prisoverslag. Endelig pris bekræftes altid i det uforpligtende tilbud baseret på adgangsforhold og flisetype.
              </p>
            </div>
          </div>

          <div className="space-y-2.5 pt-4">
            <button
              type="button"
              onClick={handleApplyToForm}
              className="w-full py-3.5 px-4 rounded-full bg-cyan-500 hover:bg-cyan-400 text-white font-bold text-sm text-center transition-all transform active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Få et tilbud med denne beregning</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </button>

            <a
              href="tel:30146070"
              className="w-full py-2 text-slate-300 hover:text-white font-medium text-xs text-center transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
              <span className="whitespace-nowrap">Eller ring direkte: 30 14 60 70</span>
            </a>
          </div>
        </div>

        {/* Right Side: Controls (Area & Presets) */}
        <div className="lg:col-span-7 space-y-6">
          {/* Step 1: Area */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label htmlFor="area-input" className="font-bold text-slate-900 text-sm sm:text-base flex items-center gap-2">
                <span>Hvor mange m² fliser skal renses?</span>
              </label>
              <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-slate-300 font-bold text-cyan-700">
                <input
                  id="area-input"
                  type="number"
                  min="10"
                  max="500"
                  value={areaM2}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    if (val >= 0 && val <= 1000) setAreaM2(val);
                  }}
                  className="w-16 text-right font-bold text-slate-900 focus:outline-none"
                />
                <span className="text-sm text-slate-500 font-normal">m²</span>
              </div>
            </div>

            {/* Slider */}
            <input
              type="range"
              min="10"
              max="250"
              step="5"
              value={areaM2}
              onChange={(e) => setAreaM2(Number(e.target.value))}
              className="w-full h-2.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-cyan-600"
            />
            <div className="flex justify-between text-xs text-slate-400 mt-1 font-medium">
              <span>10 m²</span>
              <span>100 m²</span>
              <span>250 m²</span>
            </div>

            {/* Presets */}
            <div className="mt-4 pt-3 border-t border-slate-200/70">
              <span className="text-xs text-slate-500 font-medium block mb-2">Hurtige forslag:</span>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {presets.map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => setAreaM2(preset.m2)}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold text-center border transition-all ${
                      areaM2 === preset.m2
                        ? "bg-cyan-600 text-white border-cyan-600 shadow-sm"
                        : "bg-white text-slate-700 border-slate-200 hover:border-cyan-400 hover:bg-cyan-50/50"
                    }`}
                  >
                    <div>{preset.m2} m²</div>
                    <div className="text-[10px] opacity-80 truncate">{preset.label}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Step 2: Choose Package */}
          <div className="space-y-3">
            <label className="font-bold text-slate-900 text-sm sm:text-base flex items-center gap-2">
              <span>Vælg behandlingspakke</span>
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {FLISERENS_PACKAGES.map((pkg) => {
                const isSelected = selectedPackageId === pkg.id;
                return (
                  <div
                    key={pkg.id}
                    onClick={() => setSelectedPackageId(pkg.id)}
                    className={`relative cursor-pointer rounded-none p-4 border border-slate-200 transition-all flex flex-col justify-between ${
                      isSelected
                        ? "bg-slate-100"
                        : "bg-white hover:bg-slate-50"
                    }`}
                  >
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-extrabold text-slate-900 text-base">{pkg.name}</span>
                        <div
                          className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                            isSelected
                              ? "border-slate-900 bg-slate-900 text-white"
                              : "border-slate-300"
                          }`}
                        >
                          {isSelected && <i className="fa-solid fa-check text-[9px]" aria-hidden="true" />}
                        </div>
                      </div>
                      <div className="text-xl font-extrabold text-slate-900 mb-2">
                        {pkg.pricePerM2} kr. <span className="text-xs font-normal text-slate-500">/ m²</span>
                      </div>
                      <ul className="text-xs text-slate-600 space-y-1">
                        {pkg.features.slice(0, 3).map((feat, i) => (
                          <li key={i} className="flex items-start gap-1.5">
                            <i className="fa-solid fa-check text-slate-900 text-[10px] mt-0.5 shrink-0" aria-hidden="true" />
                            <span>{feat}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
